import axios from 'axios'
import * as Sentry from '@sentry/browser'

const TAXA_AMOSTRAGEM_INDISPONIVEL = 0.1
const STATUS_INDISPONIVEL = [502, 503, 504]
const FINGERPRINT_INDISPONIVEL = 'frontend-http-indisponivel'
const FINGERPRINT_PADRAO_SDK = '{{ default }}'

const errosEnviados = new WeakSet()
const fingerprintsEnviados = new Set()

let paginaSaindo = false

function instalarListenersDePagina() {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return

  const marcarSaida = () => { paginaSaindo = true }
  const marcarVolta = () => { paginaSaindo = false }

  window.addEventListener('pagehide', marcarSaida)
  window.addEventListener('beforeunload', marcarSaida)
  window.addEventListener('pageshow', marcarVolta)
}

instalarListenersDePagina()

function redeSilenciada() {
  if (paginaSaindo) return true

  return typeof navigator !== 'undefined' && navigator.onLine === false
}

function ehCancelamento(erro) {
  if (axios.isCancel(erro)) return true

  if (erro?.code === 'ERR_CANCELED') return true

  if (erro?.message === 'Request aborted') return true

  return false
}

function ehErroHttp(erro) {
  return Boolean(erro && (erro.isAxiosError || erro.config || erro.response || erro.request))
}

function caminhoSemQuery(url) {
  if (!url) return url

  try {
    return new URL(url, window.location.origin).pathname
  } catch (erroDeParse) {
    return url
  }
}

function urlSemQueryEHash(url) {
  if (typeof url !== 'string') return url

  const indice = url.search(/[?#]/)

  return indice === -1 ? url : url.slice(0, indice)
}

function sanearHeaders(headers) {
  return ['Referer', 'referer'].reduce((acumulado, chave) => {
    if (typeof headers[chave] !== 'string') return acumulado

    return { ...acumulado, [chave]: urlSemQueryEHash(headers[chave]) }
  }, headers)
}

function sanearEvento(evento) {
  try {
    const request = evento?.request
    const temUrl = typeof request?.url === 'string'
    const temHeaders = Boolean(request?.headers) && typeof request.headers === 'object'

    if (!temUrl && !temHeaders) return evento

    const requestSaneado = { ...request }

    if (temUrl) requestSaneado.url = urlSemQueryEHash(request.url)

    if (temHeaders) requestSaneado.headers = sanearHeaders(request.headers)

    return { ...evento, request: requestSaneado }
  } catch (erroDeSaneamento) {
    return evento
  }
}

function sanearBreadcrumb(breadcrumb) {
  try {
    const dados = breadcrumb?.data

    if (!dados || typeof dados !== 'object') return breadcrumb

    const dadosSaneados = ['url', 'from', 'to'].reduce((acumulado, chave) => {
      if (typeof dados[chave] !== 'string') return acumulado

      return { ...acumulado, [chave]: urlSemQueryEHash(dados[chave]) }
    }, dados)

    return { ...breadcrumb, data: dadosSaneados }
  } catch (erroDeSaneamento) {
    return breadcrumb
  }
}

function normalizarCaminho(caminho) {
  if (!caminho) return caminho

  return caminho.replace(/\d+/g, ':id')
}

function montarFingerprint(metodo, caminho, status) {
  return ['frontend-http', metodo || 'GET', normalizarCaminho(caminho), String(status ?? 'sem-status')]
}

function fingerprintHttp(categoria, { metodo, caminho, status }) {
  if (categoria === 'indisponivel') return [FINGERPRINT_INDISPONIVEL, String(status)]

  return montarFingerprint(metodo, caminho, status)
}

function classificarPorStatus(status) {
  if (status >= 400 && status < 500) return 'esperado'

  if (STATUS_INDISPONIVEL.includes(status)) return 'indisponivel'

  if (status >= 500) return 'servidor'

  return 'nao_classificado'
}

function classificarFalhaHttp(erro) {
  if (ehCancelamento(erro)) return 'esperado'

  if (erro?.response) return classificarPorStatus(erro.response.status)

  if (erro?.code === 'ERR_NETWORK') return 'rede'

  return 'nao_classificado'
}

function classificarFalhaAjax(status, textoStatus) {
  if (textoStatus === 'abort') return 'esperado'

  if (status > 0) return classificarPorStatus(status)

  if (textoStatus === 'error') return 'rede'

  return 'nao_classificado'
}

function erroEsperado(erro) {
  return classificarFalhaHttp(erro) === 'esperado'
}

function extrairDadosHttp(erro) {
  const config = erro.config || {}

  return {
    metodo: config.method ? config.method.toUpperCase() : undefined,
    status: erro.response?.status,
    caminho: caminhoSemQuery(config.url),
    fluxo: config.fluxo,
    requestId: erro.response?.headers?.['x-request-id'],
  }
}

function registrarFalhaServidor({ fluxo, metodo, status, caminho, requestId }) {
  registrarBreadcrumb('http-erro-servidor', {
    fluxo,
    dados: { status, metodo, url: caminho, request_id: requestId },
  })
}

function registrarFalhaServidorDoErro(erro, fluxo) {
  if (errosEnviados.has(erro)) return

  const dados = extrairDadosHttp(erro)

  registrarFalhaServidor({ ...dados, fluxo: fluxo || dados.fluxo || 'http' })
  errosEnviados.add(erro)
}

function deveDescartarEvento(hint) {
  try {
    const erro = hint?.originalException

    if (!erro || typeof erro !== 'object') return false

    if (ehCancelamento(erro)) return true

    if (!ehErroHttp(erro)) return false

    const categoria = classificarFalhaHttp(erro)

    if (categoria === 'esperado' || categoria === 'servidor') return true

    return categoria === 'rede' && redeSilenciada()
  } catch (erroDeAvaliacao) {
    return false
  }
}

function montarContextoHttp(erro, categoria, { fluxo, extra } = {}) {
  const dados = extrairDadosHttp(erro)
  const fluxoFinal = fluxo || dados.fluxo || 'http'

  return {
    tags: { fluxo: fluxoFinal, http_metodo: dados.metodo, http_status: dados.status, request_id: dados.requestId },
    extra: { url: dados.caminho, ...extra },
    fingerprint: fingerprintHttp(categoria, dados),
  }
}

function montarContextoGenerico(erro, { fluxo, extra, nivel, fingerprint } = {}) {
  return {
    tags: { fluxo: fluxo || 'js' },
    extra: { ...extra },
    ...(nivel ? { level: nivel } : {}),
    ...(fingerprint ? { fingerprint } : {}),
  }
}

function capturarErro(erro, opcoes = {}) {
  if (!erro || typeof erro !== 'object') return false

  try {
    const { fluxo, extra, nivel, fingerprint } = opcoes || {}

    const categoria = classificarFalhaHttp(erro)

    if (categoria === 'esperado') return false

    if (errosEnviados.has(erro)) return false

    if (categoria === 'servidor') {
      registrarFalhaServidorDoErro(erro, fluxo)

      return false
    }

    if (categoria === 'rede' && redeSilenciada()) return false

    const contexto = ehErroHttp(erro)
      ? montarContextoHttp(erro, categoria, { fluxo, extra })
      : montarContextoGenerico(erro, { fluxo, extra, nivel, fingerprint })

    Sentry.captureException(erro, contexto)
    errosEnviados.add(erro)

    return true
  } catch (erroDeCaptura) {
    console.error('Falha ao capturar erro no monitoramento', erroDeCaptura)

    return false
  }
}

function duracaoDaRequisicao(xhr) {
  if (typeof xhr.inicioRequisicaoMs !== 'number') return undefined

  return Date.now() - xhr.inicioRequisicaoMs
}

function capturarErroAjax(xhr, settings = {}, textoStatus, erroLancado) {
  if (!xhr || typeof xhr !== 'object') return false

  if (textoStatus === 'abort') return false

  try {
    const configuracao = settings || {}
    const status = xhr.status

    const categoria = classificarFalhaAjax(status, textoStatus)
    const ehTimeout = textoStatus === 'timeout'

    if (categoria === 'esperado') return false

    if (categoria === 'nao_classificado' && !ehTimeout) return false

    if (errosEnviados.has(xhr)) return false

    const metodo = configuracao.type ? configuracao.type.toUpperCase() : undefined
    const caminho = caminhoSemQuery(configuracao.url)
    const fluxo = configuracao.fluxo || 'ajax'
    const requestId = typeof xhr.getResponseHeader === 'function' ? xhr.getResponseHeader('x-request-id') : undefined

    if (categoria === 'servidor') {
      registrarFalhaServidor({ fluxo, metodo, status, caminho, requestId })
      errosEnviados.add(xhr)

      return false
    }

    if (categoria === 'rede' && redeSilenciada()) return false

    const erro = new Error(`Falha de requisição ${metodo} ${caminho} (${status || textoStatus})`)

    if (erroLancado) erro.cause = erroLancado

    Sentry.captureException(erro, {
      tags: { fluxo, http_metodo: metodo, http_status: status || undefined, request_id: requestId },
      extra: { url: caminho, duracao_ms: duracaoDaRequisicao(xhr), tempo_na_pagina_ms: Math.round(performance.now()) },
      fingerprint: fingerprintHttp(categoria, { metodo, caminho, status: status || textoStatus }),
    })
    errosEnviados.add(xhr)

    return true
  } catch (erroDeCaptura) {
    console.error('Falha ao capturar erro ajax no monitoramento', erroDeCaptura)

    return false
  }
}

function fingerprintDoErroOriginal(erro) {
  if (!erro || typeof erro !== 'object' || !ehErroHttp(erro)) return null

  const categoria = classificarFalhaHttp(erro)

  if (categoria !== 'indisponivel' && typeof erro.config?.url !== 'string') return null

  return fingerprintHttp(categoria, extrairDadosHttp(erro))
}

function chaveDeDedupe(evento) {
  const fingerprint = evento?.fingerprint
  const fingerprintUtil = Array.isArray(fingerprint) && fingerprint.some((parte) => parte !== FINGERPRINT_PADRAO_SDK)

  if (fingerprintUtil) return fingerprint.join('|')

  const excecao = evento?.exception?.values?.[0]

  if (excecao?.type && excecao?.value) return `${excecao.type}:${excecao.value}`

  if (typeof evento?.message === 'string' && evento.message) return evento.message

  return null
}

function filtrarEvento(evento, hint) {
  try {
    if (deveDescartarEvento(hint)) {
      const erro = hint.originalException

      if (ehErroHttp(erro) && classificarFalhaHttp(erro) === 'servidor') registrarFalhaServidorDoErro(erro)

      return null
    }

    const fingerprintDoErro = fingerprintDoErroOriginal(hint?.originalException)
    const eventoFinal = fingerprintDoErro ? { ...evento, fingerprint: fingerprintDoErro } : evento
    const chave = chaveDeDedupe(eventoFinal)

    if (chave) {
      if (fingerprintsEnviados.has(chave)) return null

      fingerprintsEnviados.add(chave)
    }

    const indisponivel = eventoFinal?.fingerprint?.[0] === FINGERPRINT_INDISPONIVEL

    if (indisponivel && Math.random() >= TAXA_AMOSTRAGEM_INDISPONIVEL) return null

    return sanearEvento(eventoFinal)
  } catch (erroDeFiltro) {
    return sanearEvento(evento)
  }
}

function registrarBreadcrumb(mensagem, { fluxo, dados } = {}) {
  Sentry.addBreadcrumb({
    category: 'monitoramento',
    level: 'error',
    message: mensagem,
    data: { fluxo, ...dados },
  })
}

function instalarCapturaVue(app) {
  app.config.errorHandler = (erro, instancia, info) => {
    console.error(erro)
    capturarErro(erro, {
      fluxo: 'vue',
      extra: {
        info,
        componente: instancia?.$options?.name || instancia?.$options?.__name || 'desconhecido',
      },
    })
  }

  return app
}

export {
  erroEsperado,
  deveDescartarEvento,
  filtrarEvento,
  sanearEvento,
  sanearBreadcrumb,
  capturarErro, capturarErroAjax, registrarBreadcrumb, instalarCapturaVue }
